/**
 * SeededConfig.js
 * Generates genre-specific, probabilistic configuration profiles derived from a seed.
 * Ensures that hardcoded magic numbers are replaced by procedurally generated ranges.
 */

import { Random } from './Random.js';

const fuzz = (val, variance, random) => {
    return val + ((random.next() * variance * 2) - variance);
};

// Arrangement Strategy Matrix
const ARRANGEMENT_MATRIX = {
    // Default / Pop
    default: {
        INTRO: { drums: ['four_on_floor', 'breakbeat'], bass: ['root_fifth', 'drone'] },
        VERSE: { drums: ['basic_rock', 'tight_groove'], bass: ['root_fifth', 'stepwise'] },
        CHORUS: { drums: ['driving_rock', 'disco'], bass: ['pump', 'octaves'] },
        BRIDGE: { drums: ['tom_groove', null], bass: ['walking', 'long_tones'] },
        CLIMAX: { drums: ['driving_rock', 'trash_beat'], bass: ['octaves', 'running_eighths'] },
        OUTRO: { drums: ['breakbeat', null], bass: ['drone', 'root_fifth'] }
    },
    // Rock / Metal
    rock: {
        INTRO: { drums: ['basic_rock', 'tom_groove'], bass: ['root_fifth', 'drone'] },
        VERSE: { drums: ['basic_rock', 'half_time'], bass: ['root_fifth', 'running_eighths'] },
        CHORUS: { drums: ['driving_rock', 'punk_beat'], bass: ['gallop', 'octaves'] },
        BRIDGE: { drums: ['half_time', null], bass: ['stepwise', 'riff'] },
        CLIMAX: { drums: ['metal_blast', 'driving_rock'], bass: ['gallop', 'distortion'] },
        OUTRO: { drums: ['half_time', 'trash_beat'], bass: ['drone', 'riff'] }
    },
    // Electronic
    electronic: {
        INTRO: { drums: ['four_on_floor', 'ambient_glitch'], bass: ['drone', 'synth_pulse'] },
        VERSE: { drums: ['four_on_floor', 'breakbeat'], bass: ['offbeat', 'synth_pulse'] },
        CHORUS: { drums: ['driving_techno', 'dnb_break'], bass: ['rolling_bass', 'octaves'] },
        BRIDGE: { drums: ['ambient_glitch', null], bass: ['reese_bass', 'drone'] },
        CLIMAX: { drums: ['gabber', 'driving_techno'], bass: ['acid', 'rolling_bass'] },
        OUTRO: { drums: ['four_on_floor', null], bass: ['drone', 'synth_pulse'] }
    },
    // Orchestral / Cinematic
    cinematic: {
        INTRO: { drums: ['orchestral_swell', null], bass: ['drone', 'low_strings'] },
        VERSE: { drums: ['orchestral_march', null], bass: ['pizzicato', 'marcato'] },
        CHORUS: { drums: ['action_strike', 'epic_taiko'], bass: ['spiccato', 'octaves'] },
        BRIDGE: { drums: ['suspense_tick', null], bass: ['drone', 'swell'] },
        CLIMAX: { drums: ['trailer_hits', 'war_drums'], bass: ['staccato_runs', 'braam'] },
        OUTRO: { drums: ['orchestral_swell', null], bass: ['drone', 'low_strings'] }
    },
    // Jazz / Noir
    jazz: {
        INTRO: { drums: ['jazz_brushes', null], bass: ['walking', 'upright_sustain'] },
        VERSE: { drums: ['jazz_swing', 'brushes'], bass: ['walking', 'two_feel'] },
        CHORUS: { drums: ['jazz_ride', 'bossa'], bass: ['walking', 'latin_groove'] },
        BRIDGE: { drums: ['free_jazz', null], bass: ['solo', 'broken_time'] },
        CLIMAX: { drums: ['big_band', 'fast_swing'], bass: ['walking_fast', 'pedal'] },
        OUTRO: { drums: ['jazz_brushes', null], bass: ['walking', 'upright_sustain'] }
    },
    // Lofi / Chill
    lofi: {
        INTRO: { drums: ['lofi_beat_1', null], bass: ['lofi_bass', 'drone'] },
        VERSE: { drums: ['lofi_beat_1', 'lofi_lazy'], bass: ['lofi_bass', 'root_fifth'] },
        CHORUS: { drums: ['lofi_beat_2', 'lofi_beat_1'], bass: ['lofi_bass', 'walking'] },
        BRIDGE: { drums: ['lofi_lazy', null], bass: ['drone', 'lofi_bass'] },
        CLIMAX: { drums: ['lofi_beat_2', 'lofi_beat_1'], bass: ['walking', 'lofi_bass'] },
        OUTRO: { drums: ['lofi_beat_1', null], bass: ['drone', 'root_fifth'] }
    }
};

const resolveStrategy = (matrix, genreKey, section, random) => {
    // Determine genre key (fallback to default)
    let key = 'default';
    if (genreKey.includes('rock') || genreKey.includes('metal') || genreKey.includes('punk')) key = 'rock';
    else if (genreKey.includes('electronic') || genreKey.includes('techno') || genreKey.includes('synth')) key = 'electronic';
    else if (genreKey.includes('orchestral') || genreKey.includes('cinematic') || genreKey.includes('epic')) key = 'cinematic';
    else if (genreKey.includes('jazz') || genreKey.includes('noir')) key = 'jazz';
    else if (genreKey.includes('lofi') || genreKey.includes('chill') || genreKey.includes('study')) key = 'lofi';

    const genreSet = matrix[key] || matrix.default;
    const sectionSet = genreSet[section] || genreSet.VERSE; // Fallback to Verse

    return {
        drums: random.pick(sectionSet.drums),
        bass: random.pick(sectionSet.bass)
    };
};

// Helper to clamp values based on genre constraints
const applyConstraint = (value, constraint) => {
    if (!constraint) return value;
    let result = value;
    if (constraint.max !== undefined) result = Math.min(result, constraint.max);
    if (constraint.min !== undefined) result = Math.max(result, constraint.min);
    return result;
};

const generateOrchestrationProfile = (genre, random, densityScalar = 1.0) => {
    const g = (genre || '').toLowerCase();

    // Context-Aware Threshold Helper
    // Base: The genre default (0.0 - 1.0)
    // Density: Higher density lowers threshold (makes it easier to activate)
    // Fuzz: Random variation for uniqueness
    const getDynamicThreshold = (base, fuzzAmount = 0.05) => {
        // Density Scalar (0.0 to 2.0). 1.0 is neutral.
        // If density is 2.0 (High), we want to lower threshold.
        // Impact: +/- 0.15
        const densityMod = (densityScalar - 1.0) * -0.15;
        let t = base + densityMod;
        t = fuzz(t, fuzzAmount, random);
        return Math.max(0.05, Math.min(0.95, t));
    };

    // Default: Balanced
    const profile = {
        roleWeights: {
            melody: 0.8,
            harmony: 0.7,
            bass: 0.9,
            counter_melody: 0.4,
            percussion: 0.8
        },
        sectionAffinities: {
            INTRO: { strings: 0.8, synth: 0.8, keys: 0.8, pad: 0.9 },
            VERSE: { strings: 0.3, woodwinds: 0.4, plucked: 0.6, keys: 0.6 },
            CHORUS: { strings: 0.6, brass: 0.5, plucked: 0.8, synth: 0.7 },
            BRIDGE: { strings: 0.4, woodwinds: 0.6, keys: 0.7 },
            CLIMAX: { strings: 0.8, brass: 0.8, percussion: 0.9, synth: 0.9 },
            OUTRO: { strings: 0.5, keys: 0.6, pad: 0.8 }
        },
        activationCurves: {
            // Context-Aware & Fuzzy Thresholds
            strings: { threshold: getDynamicThreshold(0.5), slope: 1.0 },
            brass: { threshold: getDynamicThreshold(0.8), slope: 1.5 },
            woodwinds: { threshold: getDynamicThreshold(0.5), slope: 0.8 },
            percussion: { threshold: getDynamicThreshold(0.8), slope: 0.5 },
            synth: { threshold: getDynamicThreshold(0.5), slope: 1.0 },
            plucked: { threshold: getDynamicThreshold(0.3), slope: 0.8 },
            keys: { threshold: getDynamicThreshold(0.3), slope: 0.5 },
            pad: { threshold: getDynamicThreshold(0.1), slope: 0.5 }
        }
    };

    // Operation "Soft Power": Bias accumulation instead of hard override

    // 1. Cinematic Influence
    if (g.includes('cinematic') || g.includes('orchestral') || g.includes('epic')) {
        // Multiplicative Scalar (1.3x) allows 0 to remain 0 (Silence)
        profile.roleWeights.counter_melody = Math.min(1.0, profile.roleWeights.counter_melody * 1.3);

        // Influence Activation: Lower threshold for strings, raise for brass
        profile.activationCurves.strings.threshold = Math.max(0.0, profile.activationCurves.strings.threshold * 0.8);
        profile.activationCurves.brass.threshold = Math.min(0.9, profile.activationCurves.brass.threshold * 1.2);
        profile.activationCurves.brass.slope *= 1.5; // Steeper entry

        // Adjust Affinities (Weighted)
        if (profile.sectionAffinities.CLIMAX) {
            profile.sectionAffinities.CLIMAX.brass = (profile.sectionAffinities.CLIMAX.brass || 0.5) + 0.5;
            profile.sectionAffinities.CLIMAX.percussion = (profile.sectionAffinities.CLIMAX.percussion || 0.5) + 0.4;
        }
    }

    // 2. Rock/Metal Influence (Can stack with Cinematic)
    if (g.includes('rock') || g.includes('metal')) {
        profile.roleWeights.melody = Math.min(1.0, profile.roleWeights.melody * 1.2);
        profile.roleWeights.harmony = Math.min(1.0, profile.roleWeights.harmony * 1.2);

        profile.activationCurves.plucked.threshold = Math.max(0.0, profile.activationCurves.plucked.threshold * 0.7);

        // Nudge affinities
        if (profile.sectionAffinities.CHORUS) {
            profile.sectionAffinities.CHORUS.plucked = 1.0; // Guitars are essential here
            profile.sectionAffinities.CHORUS.percussion = 1.0;
        }
    }

    // 3. Electronic Influence
    if (g.includes('electronic') || g.includes('techno')) {
        profile.roleWeights.bass = 1.0; // Bass is king
        profile.activationCurves.synth.threshold = Math.max(0.0, profile.activationCurves.synth.threshold * 0.6);

        if (profile.sectionAffinities.CLIMAX) {
            profile.sectionAffinities.CLIMAX.synth = 1.0;
            profile.sectionAffinities.CLIMAX.bass = 1.0;
        }
    }

    return profile;
};

// Task 1: The "Genre Ceiling" (Style-Dependent Ranges)
const GENRE_CONSTRAINTS = {
    lofi: {
        harmonicComplexity: { max: 0.6 } // Cap complexity to keep it chill
    },
    jazz: {
        harmonicComplexity: { min: 0.3 } // Floor complexity to ensure at least 7ths
    },
    cinematic: {
        tensionSensitivity: 1.5 // Multiplier for tension input
    },
    ethereal: {
        harmonicComplexity: { max: 0.5 }, // Keep harmony simple/triadic/sustained
        rhythmicDensity: { max: 0.3 } // Force sparse rhythm
    },
    default: {}
};

const getGenreConstraints = (genre) => {
    const g = (genre || '').toLowerCase();
    if (g.includes('lofi') || g.includes('chill')) return GENRE_CONSTRAINTS.lofi;
    if (g.includes('jazz') || g.includes('noir')) return GENRE_CONSTRAINTS.jazz;
    if (g.includes('cinematic') || g.includes('epic') || g.includes('orchestral')) return GENRE_CONSTRAINTS.cinematic;
    if (g.includes('ethereal') || g.includes('ambient') || g.includes('drone')) return GENRE_CONSTRAINTS.ethereal;
    return GENRE_CONSTRAINTS.default;
};

// Task 3: The "Exponential Curve" (Input Mapping)
// Maps 0-100 linear slider to 0.0-1.0 exponential scalar
const mapInputToScalar = (input) => {
    // Safety clamp
    const val = Math.max(0, Math.min(100, input));
    // Formula: (Slider / 100) ^ 2.5
    return Math.pow(val / 100, 2.5);
};

export const getSeededConfig = (genre, seed, complexityProfile = 50) => {
    const random = new Random(seed);
    const g = (genre || '').toLowerCase();

    // Normalize Complexity Inputs using Exponential Curve
    let density = 0.5;
    let freedom = 0.5;
    let range = 0.5;
    let motion = 0.5;
    let energyInput = 0.5;

    // Support both single number (legacy wrapper) and full object
    if (typeof complexityProfile === 'number') {
        const val = mapInputToScalar(complexityProfile);
        density = val;
        freedom = val;
        range = val;
        motion = val;
        energyInput = val;
    } else {
        // Use defaults of 50 if undefined
        density = mapInputToScalar(complexityProfile.rhythmicDensity !== undefined ? complexityProfile.rhythmicDensity : 50);
        freedom = mapInputToScalar(complexityProfile.harmonicFreedom !== undefined ? complexityProfile.harmonicFreedom : 50);
        range = mapInputToScalar(complexityProfile.melodicRange !== undefined ? complexityProfile.melodicRange : 50);
        motion = mapInputToScalar(complexityProfile.timbralMotion !== undefined ? complexityProfile.timbralMotion : 50);
        energyInput = mapInputToScalar(complexityProfile.energy !== undefined ? complexityProfile.energy : 50);
    }

    // Apply Genre Constraints
    const constraints = getGenreConstraints(g);

    freedom = applyConstraint(freedom, constraints.harmonicComplexity);
    density = applyConstraint(density, constraints.rhythmicDensity);

    // Tension Sensitivity is applied later in TensionEngine usually, or here if we had a tension vector.
    // We don't have a direct 'tension' vector input here (it's usually derived or separate).
    // However, if we wanted to influence parameters based on tension sensitivity, we'd do it here.
    // The prompt says "Boost TensionSensitivity by 1.5x".
    // This likely refers to how `Tension` (GlobalContext) affects generation.
    // We can store this sensitivity in the config.
    const tensionSensitivity = constraints.tensionSensitivity || 1.0;

    // Scalar helpers (Non-Linear Mapping)
    // FIX: Multiplicative Logic for Impactful Control
    // Map 0-1 to 0.0 to 2.0 multipliers (0.5 center)

    // Density: Allow near silence at 0. Linear multiplier.
    // 0.0 -> 0.001x (Almost Silence), 0.5 -> 1.0x (Default), 1.0 -> 2.0x (Dense)
    // Updated Logic: 0.001 base + 1.999 range to prevent 0 or null bugs
    const densityScalar = 0.001 + (density * 1.999);

    // Freedom: Harmonic Complexity.
    // 0.0 -> 0.3x (Triads only), 0.5 -> 1.0x (Standard), 1.0 -> 2.0x (Jazz/Atonal)
    // Raised floor to 0.3 to allow some melodic movement even at low settings
    const freedomScalar = 0.3 + (freedom * 1.7);

    // Range: Pitch spread.
    // 0.0 -> 0.5x (Narrow), 1.0 -> 1.5x (Wide)
    const rangeScalar = 0.5 + range;

    // Motion: Timbral/Velocity dynamics.
    // 0.0 -> 0.1x (Flat), 1.0 -> 2.0x (Hyper-expressive)
    const motionScalar = 0.1 + (motion * 1.9);

    // Generate Arrangement Strategy Map
    const sections = ['INTRO', 'VERSE', 'CHORUS', 'BRIDGE', 'CLIMAX', 'OUTRO'];
    const arrangement = {};
    sections.forEach(sec => {
        arrangement[sec] = resolveStrategy(ARRANGEMENT_MATRIX, g, sec, random);
    });

    // Default Profile
    // 1. Mutation Logic
    let baseMutation = 0.15;
    if (g.includes('jazz')) baseMutation = 0.35;
    else if (g.includes('pop') || g.includes('rock')) baseMutation = 0.05;
    else if (g.includes('minimalist')) baseMutation = 0.02;

    // 2. Rhythm Logic
    // Defaults
    let strongWeight = 0.95;
    let weakWeight = 0.1;
    let syncopation = 0.1;
    let grooveProfile = 'straight';

    if (g.includes('jazz')) {
        strongWeight = 0.8;
        weakWeight = 0.15; // Reduced from 0.3 (Too spammed)
        syncopation = 0.4;
        grooveProfile = 'laid_back';
    } else if (g.includes('lofi') || g.includes('funk')) {
        strongWeight = 0.8;
        weakWeight = 0.3;
        syncopation = 0.4;
        grooveProfile = g.includes('lofi') ? 'dilla' : 'laid_back';
    } else if (g.includes('rock')) {
        strongWeight = 0.98; // Locked grid
        weakWeight = 0.05;
        syncopation = 0.05;
        grooveProfile = 'laid_back'; // Rock often drags slightly
    } else if (g.includes('techno') || g.includes('electronic')) {
        grooveProfile = 'straight';
    } else if (g.includes('punk')) {
        grooveProfile = 'rushed';
    }

    let grooveOffsets = null;
    if (grooveProfile === 'laid_back') {
         grooveOffsets = { bass: 0.015, snare: 0.02, kick: 0.0 }; // Bass drag
    } else if (grooveProfile === 'dilla') {
         grooveOffsets = { kick: 0.015, snare: -0.015, bass: 0.01 }; // Drunk
    } else if (grooveProfile === 'rushed') {
         grooveOffsets = { perc_high: -0.01, snare: -0.005 };
    }

    // 3. Harmonic Logic
    let chordToneBias = 0.9; // High strictness on strong beats
    if (g.includes('jazz') || g.includes('neo_soul')) chordToneBias = 0.6; // Allow color tones on downbeats

    const config = {
        arrangement: arrangement,
        // Procedural Strategy Vectors
        vectors: {
            // Apply scalars directly to base probability
            rhythmicDensity: density, // Pass raw density to TextureEngine
            melodicDensity: Math.min(1.0, 0.5 * densityScalar),
            harmonicComplexity: Math.min(1.0, 0.5 * freedomScalar),
            energy: Math.min(1.0, Math.max(0.0, energyInput + fuzz(0, 0.05, random))), // Use input energy
            melodicRange: range, // Raw value 0-1
            timbralMotion: motion, // Raw value 0-1
            tensionSensitivity: tensionSensitivity // Store sensitivity
        },
        mutationProfile: {
            mutationRate: Math.min(0.8, baseMutation * freedomScalar),
            anchorPoints: [0, 2]
        },
        rhythmProfile: {
            strongBeatWeight: Math.min(1.0, strongWeight * densityScalar), // Strong beats scale with density
            weakBeatWeight: Math.min(0.8, weakWeight * densityScalar), // Weak beats depend heavily on density
            syncopationChance: Math.min(0.9, syncopation * densityScalar),
            grooveProfile: grooveProfile,
            grooveOffsets: grooveOffsets
        },
        harmonicProfile: {
            strongBeatChordToneWeight: chordToneBias,
            weakBeatExtensionWeight: Math.min(0.9, 0.3 + (0.3 * freedomScalar)) // Extensions controlled by freedom
        },
        velocities: {
            base: fuzz(0.6, 0.05, random),
            accent: fuzz(0.1, 0.02, random) * motionScalar, // Dynamics controlled by motion
            ghost: fuzz(0.4, 0.05, random),
            variance: motion, // Store raw motion for jitter
            boost: 0.0
        },
        harmony: {
            rhythm: 1, // subdivisions
            // Boosted Base Probabilities for "Jailbreak"
            mutationChance: fuzz(0.25, 0.05, random) * freedomScalar, // Freedom controls mutation (Max ~50%)
            borrowedChance: fuzz(0.30, 0.05, random) * freedomScalar, // Max ~60%
            sus4Chance: fuzz(0.3, 0.1, random) * freedomScalar,
            cadenceChance: 0.9,
            secondaryDominantChance: Math.min(0.85, fuzz(0.35, 0.1, random) * freedomScalar),
            pivotModulationChance: Math.min(0.6, fuzz(0.2, 0.1, random) * freedomScalar),
            extensionChance: Math.min(0.95, fuzz(0.4, 0.1, random) * freedomScalar) // Boosted base for HarmonyManager
        },
        voicing: {
            doublingChance: Math.min(1.0, fuzz(0.8, 0.1, random) * densityScalar), // Density controls voicing thickness
            drop2Chance: Math.min(1.0, fuzz(0.5, 0.2, random) * freedomScalar)
        },
        orchestration: {
            percFocusChance: Math.min(1.0, fuzz(0.3, 0.1, random) * densityScalar),
            melodicFocusChance: Math.min(1.0, fuzz(0.3, 0.1, random) * densityScalar),
            phraseStructure: random.pick(['sentence', 'period', 'free']),
            activityMasking: {
                highThreshold: Math.min(0.95, fuzz(0.7, 0.1, random) + (density * 0.2)), // High density -> Higher mask threshold (allow more notes)
                lowThreshold: fuzz(0.2, 0.05, random)
            },
            dynamicHairpins: motion > 0.3, // Enable if motion is decent
            agogicAccents: random.bool(0.7),
            ...generateOrchestrationProfile(g, random, densityScalar)
        },
        sciFi: {
            highArp: {
                rangeMin: random.range(65, 75),
                rangeMax: random.range(90, 100),
                pattern: [{s:0,l:0.25},{s:0.25,l:0.25},{s:0.5,l:0.25},{s:0.75,l:0.25}]
            }
        },
        progression: {
            baroqueSequence: [0, 3, 6, 2, 5, 1, 4, 0],
            romanticSubstitutions: { degree: 4, targets: [1, 3] },
            epicShiftChance: fuzz(0.7, 0.1, random),
            darkTension: { template: 'dark_tension' },
            americana: { template: 'americana_folk' },
            gothic: { progressionPreference: 'minor_harmonic' }
        }
    };

    // Genre Overrides
    if (g.includes('jazz') || g.includes('noir') || g.includes('swing')) {
        // Increase swing emphasis
        config.rhythmProfile.syncopationChance = 0.5;
    }

    if (g.includes('epic') || g.includes('cinematic')) {
        config.velocities.base = fuzz(0.7, 0.05, random);
        config.velocities.accent = fuzz(0.15, 0.03, random);
        config.harmony.mutationChance = fuzz(0.3, 0.1, random); // More modal interchange
        config.voicing.doublingChance = 1.0; // Always double
        config.orchestration.percFocusChance = 0.6; // High prob of perc heavy
    }
    else if (g.includes('minimalist')) {
        config.harmony.rhythm = 8.0; // Very slow harmonic rhythm (1 chord per 8 bars)
        config.velocities.base = fuzz(0.55, 0.02, random); // Consistent
        config.harmony.mutationChance = 0.05; // Static harmony
        config.orchestration.melodicFocusChance = 0.1; // Minimal variation
        config.vectors.timbralMotion = Math.min(config.vectors.timbralMotion, 0.3); // Force flat dynamics
    }
    else if (g.includes('fantasy')) {
        config.orchestration.dynamicHairpins = true; // Force swells
        config.velocities.variance = Math.max(0.2, config.velocities.variance);
        config.orchestration.melodicFocusChance = 0.7;
    }
    else if (g.includes('horror') || g.includes('tension')) {
        config.harmony.borrowedChance = fuzz(0.6, 0.1, random); // Lots of dissonance
        config.velocities.base = fuzz(0.4, 0.2, random); // Unpredictable dynamic range
    }
    else if (g.includes('rock') || g.includes('metal')) {
        config.velocities.base = fuzz(0.8, 0.05, random);
        config.velocities.boost = 0.1;
        config.orchestration.percFocusChance = Math.min(1.0, 0.8 * densityScalar); // Very percussive, scaled
    }
    else if (g.includes('romantic')) {
        config.velocities.base = fuzz(0.55, 0.1, random); // Lower base to allow more headroom for swells
        config.velocities.variance = Math.max(0.2, config.velocities.variance); // Ensure variance is at least 0.2
        config.harmony.sus4Chance = 0.6;
        config.orchestration.percFocusChance = 0.1; // Low percussion
        config.orchestration.melodicFocusChance = 0.8; // High melody focus
        config.orchestration.dynamicHairpins = true; // Force hairpins
    }
    else if (g.includes('lofi')) {
        config.velocities.base = fuzz(0.5, 0.1, random); // Low velocity
        config.velocities.ghost = fuzz(0.3, 0.1, random); // Quiet ghosts
        config.harmony.mutationChance = fuzz(0.4, 0.1, random); // Jazz influence
        config.orchestration.percFocusChance = 0.9; // Beat driven
        config.voicing.doublingChance = 0.2; // Sparse
    }

    return config;
};

/**
 * Task 1: The "Safe Zone" Governor (Input Veto)
 * Extracts strict constraints from the configuration.
 * @param {Object} context - Generation context containing config.
 * @returns {Object} Constraints object.
 */
export const getGlobalConstraints = (context) => {
    // Robust extraction: Context -> Config -> Vectors
    const config = context.config || {};
    const vectors = config.vectors || {};

    // Get Raw Scalars (0.0 - 1.0)
    // Freedom = Harmonic Complexity
    const freedom = vectors.harmonicComplexity !== undefined ? vectors.harmonicComplexity : 0.0;

    // Rhythm = Rhythmic Density (or Melodic if fallback)
    const density = (vectors.rhythmicDensity !== undefined ? vectors.rhythmicDensity : (vectors.melodicDensity !== undefined ? vectors.melodicDensity : 0.0));

    // The Logic: "Safe Zone" Vetoes
    return {
        // Safe Zone (0-15%): If HarmonicFreedom < 0.15, VETO all Non-Triad tones. Force strictly Diatonic.
        // Updated from 0.4 to 0.15 to resolve calibration issue where medium settings felt too safe.
        forceDiatonic: freedom < 0.15,

        // Rhythm Guard: If RhythmicDensity < 0.3, VETO 16th notes. Force 8th/Quarter notes only.
        forceSimpleRhythm: density < 0.3
    };
};
